import React, { useContext, useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { UserContext } from '../UserAndProfile/UserContext';
import PaymentForm from './PaymentForm';
import { trackPixelEvent, PixelEvents } from '../../utils/meta/metaPixel';
import { stripePromise } from '../../utils/meta/stripePromise';
import { isFreeModeEnabled } from '../../utils/accessMode';

const TrialConversionPage: React.FC = () => {
  const theme = useTheme();
  const { user, loading: userLoading } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isFreeMode = isFreeModeEnabled();
  
  const emailFromUrl = searchParams.get('email');
  const utmSource = searchParams.get('utm_source');
  const utmCampaign = searchParams.get('utm_campaign');
  
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Track that user arrived from email/marketing
    if (utmSource) {
      trackPixelEvent(PixelEvents.VIEW_CONTENT, {
        content_name: 'Upgrade Payment Page',
        content_category: 'Payment Flow',
        utm_source: utmSource,
        utm_campaign: utmCampaign || 'unknown',
      });
    }
  }, [utmSource, utmCampaign]);

  const handlePaymentComplete = (data: { subscriptionId: string; customerId: string; userId: string }) => {
    // Track successful payment
    trackPixelEvent(PixelEvents.PURCHASE, {
      content_name: 'Monthly Subscription',
      content_category: 'SAT Tutoring',
      currency: 'USD',
      value: 39.99,
      subscription_id: data.subscriptionId,
      customer_id: data.customerId,
    });

    // Redirect to dashboard with success message
    navigate('/sat', { 
      state: { 
        showUpgradeSuccess: true,
        userName: user?.firstName || undefined
      } 
    });
  };

  // Loading state
  if (userLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: theme.palette.custom.beige 
      }}>
        <CircularProgress />
      </Box>
    );
  }

  // User not logged in
  if (!user) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: theme.palette.custom.beige,
        p: 4
      }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h5" gutterBottom>
            Please Log In
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You need to be logged in to upgrade your subscription.
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/login', { state: { returnTo: '/upgrade' } })}
            sx={{ mr: 2 }}
          >
            Log In
          </Button>
          <Button 
            variant="outlined" 
            onClick={() => navigate('/signup')}
          >
            Sign Up
          </Button>
        </Paper>
      </Box>
    );
  }

  // User already has active subscription
  if (user.subscriptionStatus === 'active') {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        bgcolor: theme.palette.custom.beige,
        p: 4
      }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 400 }}>
          <Typography variant="h5" gutterBottom>
            Already Subscribed
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You already have an active subscription. Continue your SAT prep!
          </Typography>
          <Button 
            variant="contained" 
            onClick={() => navigate('/sat')}
          >
            Go to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  if (isFreeMode) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', bgcolor: theme.palette.custom.beige, p: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center', maxWidth: 500 }}>
          <Typography variant="h5" gutterBottom>
            Free Access Is Enabled
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Billing is currently turned off, so you can continue using all SAT prep features without upgrading.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/sat')}>
            Go to Dashboard
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: { xs: 'column', md: 'row' }, 
      minHeight: '100vh', 
      bgcolor: theme.palette.custom.beige 
    }}>
      {/* Left Column - Value Proposition */}
      <Box sx={{ 
        flex: 1, 
        p: { xs: 4, md: 8 }, 
        display: 'flex', 
        flexDirection: 'column', 
        bgcolor: theme.palette.custom.beige,
        marginTop: { xs: '5vh', md: '10vh' } 
      }}>
        {/* Main Value Proposition */}
        <Box sx={{ mb: { xs: 4, sm: 6 } }}>
          <Typography 
            variant="h2" 
            component="h1" 
            sx={{ 
              fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' }, 
              fontWeight: 'bold', 
              mb: 2, 
              color: theme.palette.text.primary 
            }}
          >
            Continue Your SAT Success Journey
          </Typography>
          <Typography 
            component="span" 
            sx={{ 
              fontSize: { xs: '1.25rem', md: '1.5rem' }, 
              fontWeight: 'bold', 
              color: theme.palette.primary.main 
            }}
          >
            Upgrade to Lifetime Access for $120 or Start with $39.99/Month
          </Typography>
        </Box>
        
        {/* Benefits List */}
        <Box sx={{ mt: 0 }}>
          <Typography 
            variant="h5" 
            sx={{ 
              fontSize: { xs: '1.5rem', md: '1.75rem' }, 
              fontWeight: 'bold', 
              mb: 3, 
              color: theme.palette.text.primary 
            }}
          >
            What You Get:
          </Typography>
          
          {[
            'Unlimited practice questions with detailed explanations',
            'Personalized study plans based on your progress',
            'Full-length practice tests with scoring',
            'AI-powered tutoring available 24/7',
            'Progress tracking and analytics',
            'Mobile access for studying anywhere'
          ].map((benefit, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
              <Box 
                sx={{ 
                  width: 8, 
                  height: 8, 
                  borderRadius: '50%', 
                  bgcolor: theme.palette.primary.main, 
                  mt: 1, 
                  mr: 2,
                  flexShrink: 0
                }} 
              />
              <Typography 
                variant="body1" 
                sx={{ 
                  fontSize: { xs: '1rem', md: '1.125rem' }, 
                  color: theme.palette.text.secondary 
                }}
              >
                {benefit}
              </Typography>
            </Box>
          ))}
        </Box>

        {/* Trust Indicators */}
        <Box sx={{ mt: 4 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: theme.palette.text.secondary,
              fontStyle: 'italic'
            }}
          >
            ✓ Cancel anytime • ✓ Secure payment • ✓ Thousands of satisfied students
          </Typography>
        </Box>
      </Box>

      {/* Right Column - Payment Form */}
      <Box sx={{ 
        flex: 1, 
        p: { xs: 4, md: 8 }, 
        display: 'flex', 
        flexDirection: 'column', 
        justifyContent: 'center',
        bgcolor: theme.palette.background.default,
        minHeight: { xs: 'auto', md: '100vh' }
      }}>
        <Container maxWidth="sm" sx={{ width: '100%' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Welcome Message */}
          <Box sx={{ textAlign: 'center', mb: 0, mt: '3vh' }}>
            <Typography variant="h4" gutterBottom>
              Welcome back, {user.firstName}!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Get the Scores and Scholarships You Need <br />with Personalized AI-Powered SAT Prep Today!
            </Typography>
          </Box>

          {/* Payment Form */}
          <Elements stripe={stripePromise}>
            <PaymentForm
              user={{
                uid: user.uid,
                email: user.email,
                firstName: user.firstName,
                lastName: user.lastName
              }}
              onComplete={handlePaymentComplete}
              paymentType={'monthly'}
            />
          </Elements>

          {/* Back to Dashboard Link */}
          <Box sx={{ textAlign: 'center', mt: 3 }}>
            <Button 
              variant="text" 
              onClick={() => navigate('/sat')}
              sx={{ color: theme.palette.text.secondary }}
            >
              ← Back to Dashboard
            </Button>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default TrialConversionPage;